import type { PlaceStat } from "../types";

type Props = {
  stats: PlaceStat[];
};

function Spark({ values }: { values: number[] }) {
  if (values.length < 2) return null;
  const max = Math.max(...values, 1);
  const w = 220;
  const h = 56;
  const step = w / (values.length - 1);
  const d = values
    .map((v, i) => `${i === 0 ? "M" : "L"} ${(i * step).toFixed(1)} ${(h - (v / max) * (h - 6) - 3).toFixed(1)}`)
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="pr-spark h-14 w-full" aria-hidden>
      <path d={d} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function sum(list: PlaceStat[]): number {
  return list.reduce((n, s) => n + s.value, 0);
}

function mergeSeries(list: PlaceStat[]): number[] {
  const len = Math.max(0, ...list.map((s) => (s.series || []).length));
  const out = Array.from({ length: len }, () => 0);
  list.forEach((s) => (s.series || []).forEach((n, i) => (out[i] += n)));
  return out;
}

export default function StatsPanel({ stats }: Props) {
  if (stats.length === 0) {
    return <p className="pr-card px-6 py-16 text-center text-sm text-slate-500">Aucune statistique pour cette fiche.</p>;
  }
  const max = Math.max(...stats.map((s) => s.value), 1);
  const views = stats.filter((s) => s.group === "vues");
  const actions = stats.filter((s) => s.group === "actions");
  const blocks = [
    { title: "Vues", hint: "Recherche et Maps", items: views.length ? views : stats },
    { title: "Actions", hint: "Appels, site, itinéraires", items: actions },
  ].filter((b) => b.items.length > 0);

  return (
    <div className="space-y-5">
      <div className="grid gap-5 lg:grid-cols-2">
        {blocks.map((b) => (
          <article key={b.title} className="pr-card p-6">
            <p className="text-sm text-slate-500">{b.title}</p>
            <p className="mt-2 text-4xl font-semibold tracking-tight">{sum(b.items)}</p>
            <p className="mt-1 text-xs uppercase tracking-wide text-slate-400">{b.hint} · 28 jours</p>
            <div className="mt-4">
              <Spark values={mergeSeries(b.items)} />
            </div>
          </article>
        ))}
      </div>
      <section className="pr-card space-y-5 p-6">
        <div>
          <h3 className="text-lg font-semibold">Détail</h3>
          <p className="mt-1 text-sm text-slate-500">Chaque barre compare les sources entre elles.</p>
        </div>
        {stats.map((s) => (
          <div key={s.id}>
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <p className="text-sm font-medium">{s.label}</p>
              <p className="text-sm font-semibold tabular-nums">{s.value}</p>
            </div>
            <div className="pr-bar">
              <span style={{ width: `${Math.max(6, Math.round((s.value / max) * 100))}%` }} />
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
